import { Box, Paper, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../../components/button/RefreshButton";
import ResizableBox from "../../../components/container/ResizableBox";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import ContextMenu from "../../../components/context-menu/ContextMenu";
import ExportAsXLSXButton from "../../../components/export/ExportAsXLSXButton";
import AgGrid from "../../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../../components/input/SearchBox";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { TemplateSharing } from "../../../db_access/template_sharing";

const PrebuiltTemplateGrid = ({ type }) => {
  const { t } = useTranslation();
  const { refresh, setRefresh, columnVisibilities, enqueueAlert } = useContextProvider();
  const gridRef = useRef(null);
  const gridHeaderContainerRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [searchText, setSearchText] = useState("");
  const colVisibility_key = "prebuilt_template";
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);

  const colDefs = useMemo(() => {
    const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
    const types = {
      product: "Product",
      project: "Project",
      building: "Building",
      supply_chain: "Supply Chain",
      structure: "Organization",
    };
    return [
      { headerName: "Name", field: "name", cellStyle: { fontWeight: 500, ...leftAlignColDef.cellStyle }, ...defaultColDef, headerCheckboxSelection: true },
      !type && {
        headerName: "Type",
        field: "type",
        ...defaultColDef,
        ...leftAlignColDef,
        valueGetter: (params) => {
          const raw = params.data?.type;
          return types[raw] ?? raw ?? "";
        },
      },
      { headerName: "Year", field: "year", ...defaultColDef, ...centerAlignColDef },
      { headerName: "GWP (kg CO₂e)", field: "gwp", cellStyle: { fontWeight: 500, ...rightAlignColDef.cellStyle }, ...defaultColDef, ...floatFormater },
      { headerName: "Biogenic GWP (kg CO₂e)", field: "biogenic_gwp", cellStyle: { fontWeight: 500, ...rightAlignColDef.cellStyle }, ...defaultColDef, ...floatFormater },
      { headerName: "Date Modified", field: "date_modified", ...defaultColDef, ...leftAlignColDef },
    ]
      .filter(Boolean)
      .map((colDef) => ({ ...colDef, hide: isHidden(colDef.field) }));
  }, [columnVisibilities, type]);
  const defaultCols = useMemo(() => colDefs.map((col) => col.field), [colDefs]);

  const displayedTemplates = useMemo(() => {
    if (type) {
      return templates.filter((template) => template.type === type);
    }
    return templates;
  }, [templates, type]);
  useEffect(() => {
    TemplateSharing.get_by_my_company()
      .then((res) => res.json())
      .then((data) => setTemplates(data.map((item, index) => ({ ...item, id: index }))));
  }, [refresh]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  const onSelectionChanged = useCallback(
    (event) => {
      const selected = event.api.getSelectedRows();
      setSelectedTemplates(selected);
    },
    [setSelectedTemplates]
  );
  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuPosition({ x: e.pageX, y: e.pageY });
    setMenuVisible(true);
  };
  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  const retrieveToWorkspace = useCallback(
    (selectedItems) => {
      TemplateSharing.retrieve_to_workspace(selectedItems)
        .then((responses) => {
          const allOk = responses.every((r) => r && (r.ok ?? r.status === 200));
          if (allOk) {
            enqueueAlert({ message: "Retrieve successful" });
          } else {
            enqueueAlert({ message: "Retrieve failed", severity: "error" });
          }
        })
        .finally(() => {
          setSelectedTemplates([]);
          setRefresh((r) => !r);
        });
    },
    [setRefresh]
  );
  const contextMenuOptions = useMemo(() => [{ label: "Retrieve to Workspace", action: retrieveToWorkspace, needSelectedItems: true }], [retrieveToWorkspace]);

  return (
    <Box>
      <Paper variant="Table Container">
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {t("Prebuilt Library")} ({displayedTemplates.length})
            </Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
            <SearchBox text={searchText} setText={setSearchText} />
            <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
            <ExportAsXLSXButton data={displayedTemplates} label="Export as .xlsx" filename="templates.xlsx" />
            <RefreshButton />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box>
          <ResizableBox maxHeight={900}>
            <AgGrid
              rowData={displayedTemplates}
              columnDefs={colDefs}
              onSelectionChanged={onSelectionChanged}
              handleContextMenu={handleContextMenu}
              headerCheckbox={false}
              ref={gridRef}
            />
          </ResizableBox>
        </Box>
      </Paper>
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={selectedTemplates} options={contextMenuOptions} />
    </Box>
  );
};
export default PrebuiltTemplateGrid;
